import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import {
  NewFeatureBadge,
  TailwindTabs,
  TailwindTabsContent,
  TailwindTabsList,
  TailwindTabsTrigger,
  useTrackModuleViews,
} from '@autone/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';

import { AssortmentDataFeedTooltip } from '../../../new/features/data-feed/AssortmentDataFeedTooltip';
import { APP_NAME } from '../../../old/consts';
import { AssortmentDocsLink } from '../../../old/features/assortment-docs-link';
import { AssortmentExtrasMenu } from '../../../old/features/assortment-extras-menu';
import { AssortmentFilterChips } from '../../../old/features/assortment-filters';
import AssortmentFilterButton from '../../../old/features/assortment-filters/AssortmentFilterButton';
import AssortmentSearchInput from '../../../old/features/assortment-filters/AssortmentSearchInput';
import { AssortmentTemplateModals } from '../../../old/features/assortment-template-modals';
import { ProductsTable } from '../../../old/features/tables/products-table';
import { SkuLocationsTable } from '../../../old/features/tables/sku-locations-table';
import { AssortmentLayout } from '../../../old/layout';
import { inSeasonSearchTermAtom } from '../../../old/storage/atoms';
import {
  type AssortmentTableNames,
  useAssortmentSessionStorageAtom,
} from '../../../old/storage/session-storage';
type InSeasonViewTableNames = Extract<
  AssortmentTableNames,
  'productsTable' | 'skuLocationsTable'
>;

export function InSeasonView() {
  const { configReleaseAssortmentExplorerTab } = useFlags();
  const { t, common } = useAutoneTranslation<typeof APP_NAME>();
  const [openTab, setOpenTab] =
    useAssortmentSessionStorageAtom('currentViewTab');

  const [searchTerm, setSearchTerm] = useAtom(inSeasonSearchTermAtom);

  useTrackModuleViews({
    app: APP_NAME,
    view: 'in-season view',
    viewMode: 'main',
  });

  useEffect(() => {
    trackAnalyticsEvent({
      category: 'assortment_tables',
      action:
        openTab === 'productsTable'
          ? 'product_view_table:table_tab_clicked'
          : 'sku_locations_table:table_tab_clicked',
    });
  }, [openTab]);

  return (
    <AssortmentLayout
      headerText={t('pages.in-season.header')}
      subHeaderText={
        <div className="flex gap-1 items-baseline">
          <p>{t('pages.in-season.sub-header')}</p>
          {'·'}
          <AssortmentDocsLink />
        </div>
      }
      subHeaderExtras={<AssortmentDataFeedTooltip />}
      rightExtras={
        <div className="flex flex-col gap-2 items-end">
          <div className="flex flex-row self-end gap-2 h-full">
            <AssortmentTemplateModals />
          </div>
        </div>
      }
    >
      <TailwindTabs
        value={openTab}
        onValueChange={(v) => setOpenTab(v as InSeasonViewTableNames)}
        className="flex flex-col gap-0 bg-white rounded-md overflow-hidden shadow-sm border border-grey-5"
      >
        {/** Tabs List */}
        <TailwindTabsList
          variant={'underline'}
          className="flex flex-row gap-2 w-full justify-start bg-grey-10"
          size={'lg'}
        >
          <TailwindTabsTrigger
            id="in-season-view-products-table-tab"
            data-testid="products-table-tab"
            className="w-fit px-6"
            value="productsTable"
            variant={'underline'}
          >
            {common('products')}
          </TailwindTabsTrigger>
          <TailwindTabsTrigger
            id="in-season-view-explorer-tab"
            data-testid="sku-locations-table-tab"
            className="flex gap-2 w-fit px-6"
            value="skuLocationsTable"
            variant={'underline'}
          >
            {common('explorer')}
            <NewFeatureBadge releaseDate={configReleaseAssortmentExplorerTab} />
          </TailwindTabsTrigger>
        </TailwindTabsList>

        {/** Table Controls */}
        <div className="flex flex-row justify-between items-center gap-8 w-full p-4">
          <div className="flex flex-row gap-2">
            <AssortmentExtrasMenu tableName={openTab} align={'start'} />
            <AssortmentFilterButton align="start" tableName={openTab} />
            <AssortmentSearchInput
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
            />
          </div>
          <AssortmentFilterChips tableName={openTab} />
        </div>

        {/** Tabs Content */}
        <TailwindTabsContent className="m-0" value="productsTable">
          <ProductsTable />
        </TailwindTabsContent>
        <TailwindTabsContent className="m-0" value="skuLocationsTable">
          <SkuLocationsTable />
        </TailwindTabsContent>
      </TailwindTabs>
    </AssortmentLayout>
  );
}

export const Route = createFileRoute('/(old)/in-season/')({
  component: InSeasonView,
});
